import { getErrorReporter } from './error-reporting.js';

export type EnrichHotelPhotosFlow = (input: { hotelId: string }) => Promise<unknown>;

/**
 * Tracks in-flight enrichment requests per hotelId so concurrent renders (widget + card + guide
 * for the same hotel, on either platform) coalesce into a single server call — the server, in
 * turn, hits the media provider at most once per hotel.
 */
const inFlight = new Map<string, Promise<unknown>>();

/** Calls the `enrichHotelPhotos` flow at most once per `hotelId` while a call is already in flight. */
export async function enrichHotelPhotosOnce(
  enrichHotelPhotos: EnrichHotelPhotosFlow,
  hotelId: string,
): Promise<unknown> {
  const existing = inFlight.get(hotelId);
  if (existing) return existing;
  const promise = enrichHotelPhotos({ hotelId })
    .catch((error: unknown) => {
      getErrorReporter().captureException(error);
    })
    .finally(() => {
      inFlight.delete(hotelId);
    });
  inFlight.set(hotelId, promise);
  return promise;
}

// ── Platform port ───────────────────────────────────────────────────────────
// HotelModel.allImages triggers enrichment from inside the getter (the same shape the old
// Supabase fallback had), so EVERY surface that renders hotel images — web and mobile alike —
// is covered without per-component wiring. The flow transport is injected at composition time
// (FeaturesContainer); until then requests are no-ops.

let enricher: EnrichHotelPhotosFlow | undefined;
const attempted = new Set<string>();

/** Injected by FeaturesContainer once the flow transport exists. */
export function setHotelPhotoEnricher(flow: EnrichHotelPhotosFlow | undefined): void {
  enricher = flow;
}

/**
 * Fire-and-forget photo backfill for a hotel rendered without stored photos. At most one
 * attempt per hotel per session (the server's `imagesMeta` guard covers cross-session); safe
 * to call from a render-path getter.
 */
export function requestHotelPhotoEnrichment(hotelId: string): void {
  if (!hotelId || !enricher || attempted.has(hotelId)) return;
  attempted.add(hotelId);
  void enrichHotelPhotosOnce(enricher, hotelId);
}
